import { useEffect, useMemo, useState } from "react";
import { tryEnterFullscreen } from "../local/fullscreen";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { AvatarConfig } from "@shared/types";
import { useLocalProfile } from "../local/useLocalProfile";
import { useMpRoom } from "../multiplayer/useMpRoom";
import { getBattlePlayerId } from "../multiplayer/battleIdentity";
import { newRoomCode } from "../multiplayer/types";
import { AvatarPreview } from "../components/AvatarEditor";
import { AvatarViewer3D } from "../components/AvatarViewer3D";
import { SEED_LESSONS } from "../data/seed-lessons";
import { BattlePackPicker } from "./BattlePackPicker";
import { getWordPack } from "../data/word-packs";
import { getTeachingLevel } from "../data/teaching-levels";
import { QUIZ_STYLES, getQuizStyle } from "../data/quizStyles";
import { isLessonUnlocked } from "../local/profile";
import { SINGLE_COURSE_SEED } from "../game/courseConfig";
import { MAX_PLAYERS } from "../multiplayer/protocol";
import { clearSession } from "../local/session";
import { RacePlay } from "./RacePlay";

/** Shared shape for embedding the race flow inside a class session — see
 *  QuizzleController/QuizzleHost's identical `codeOverride`/`identity`/
 *  `embedded` pattern. When embedded, phase transitions render the next
 *  component directly (RaceLobby → RacePlay → RaceResults → …) instead of
 *  navigating the browser, so the outer ClassRoom connection never drops. */
export interface RaceEmbedProps {
  codeOverride?: string;
  identity?: { id: string; displayName: string; avatarConfig: AvatarConfig };
  embedded?: boolean;
  /** Embedded only: true if this identity should host (start/change level). */
  asHost?: boolean;
}

/** Small fullscreen toggle for the battle-lobby top bar. Keeps the icon
 *  in sync with `document.fullscreenElement`. Tapping it requests / exits
 *  fullscreen on the document root. Silently does nothing on iOS Safari
 *  (no Fullscreen API). */
function LobbyFullscreenButton() {
  const [isFs, setIsFs] = useState(typeof document !== "undefined" && !!document.fullscreenElement);
  useEffect(() => {
    const onFs = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);
  const toggle = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else tryEnterFullscreen();
    } catch {
      /* iOS Safari + others — ignore */
    }
  };
  return (
    <button
      className="lobby-fs-btn"
      onClick={toggle}
      aria-label={isFs ? "Exit fullscreen" : "Enter fullscreen"}
      title={isFs ? "Exit fullscreen" : "Enter fullscreen"}
    >
      {isFs ? "⤓" : "⛶"}
    </button>
  );
}

/**
 * Battle lobby — visually echoes the main menu (card-on-gradient, big
 * rounded buttons, friendly copy) so kids stay in the same world feel
 * across the app.
 *
 *   /battle                    → entry (Create / Join)
 *   /battle?code=XXXX&host=1   → host: lobby (lesson set by host)
 *   /battle?code=XXXX          → joiner: lobby (waiting room)
 */
export function RaceLobby({ codeOverride, identity, embedded, asHost: asHostProp }: RaceEmbedProps = {}) {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { profile } = useLocalProfile();
  const code = codeOverride ?? params.get("code");
  const asHost = embedded ? !!asHostProp : params.get("host") === "1";
  const visibility: "open" | "private" = params.get("vis") === "open" ? "open" : "private";
  // Persist the host's chosen lesson per room code so coming back to the
  // lobby (via the post-race "Back to Lobby" / "Play Again" / "Next
  // Level" flows) doesn't re-prompt for the level picker. Joining
  // devices don't need this — they inherit state.lessonId from the
  // server snapshot.
  const lessonStorageKey = code ? `wr_battle_lesson_${code}` : null;
  const [chosenLessonId, setChosenLessonIdState] = useState<string | null>(() => {
    if (typeof window === "undefined" || !lessonStorageKey) return null;
    try { return sessionStorage.getItem(lessonStorageKey); } catch { return null; }
  });
  const setChosenLessonId = (id: string | null) => {
    setChosenLessonIdState(id);
    if (typeof window === "undefined" || !lessonStorageKey) return;
    try {
      if (id) sessionStorage.setItem(lessonStorageKey, id);
      else sessionStorage.removeItem(lessonStorageKey);
    } catch { /* private mode — ignore */ }
  };
  const [changeLessonOpen, setChangeLessonOpen] = useState(false);

  if (!embedded && !profile) {
    nav("/", { replace: true });
    return null;
  }

  const me = identity ?? {
    id: getBattlePlayerId(profile!.id, code, asHost ? "host" : "join"),
    displayName: profile!.displayName,
    avatarConfig: profile!.avatarConfig,
  };
  const lessons = useMemo(
    () => [...SEED_LESSONS].sort((a, b) => a.orderIndex - b.orderIndex),
    [],
  );

  const joinOpts = useMemo(() => {
    if (!code) return null;
    if (asHost && !chosenLessonId) return null;
    return {
      code,
      asHost,
      me,
      lessonId: chosenLessonId ?? undefined,
      seed: asHost ? SINGLE_COURSE_SEED : undefined,
      visibility,
    };
  }, [code, asHost, me.id, chosenLessonId, visibility]);

  const { client, state, error } = useMpRoom(joinOpts);

  // The server is the source of truth for the active lesson — if a
  // "Next Level" / "Change Lesson" updated it since we last set our
  // local chosenLessonId, sync our local cache so the lobby reads
  // consistently and the persisted value survives a refresh.
  useEffect(() => {
    if (state?.lessonId && state.lessonId !== chosenLessonId) {
      setChosenLessonId(state.lessonId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.lessonId]);

  // 1) No code yet → entry screen (never happens when embedded — the class
  //    always supplies codeOverride).
  if (!code) return embedded ? null : <RaceEntry />;

  // 2) Host hasn't picked a lesson yet → pack + level picker.
  if (asHost && !chosenLessonId) {
    return (
      <BattlePackPicker
        onPick={(id) => setChosenLessonId(id)}
        onCancel={() => { if (!embedded) nav("/battle"); }}
      />
    );
  }

  // 3) Connecting / failed.
  if (error) {
    return (
      <div className="shell-narrow stack lobby-shell">
        <h1 className="t-display t-center">Room not found</h1>
        <p className="t-body t-muted t-center">{error}</p>
        {!embedded && (
          <button className="btn btn-primary btn-big btn-block" onClick={() => nav("/battle")}>
            Back
          </button>
        )}
      </div>
    );
  }
  if (!state) return <div className="full-center">Joining…</div>;

  // 4) Race started → bounce to play. Embedded: render RacePlay directly
  //    (same underlying room connection, keyed by code+id) instead of
  //    navigating — the class connection stays alive the whole time.
  if (state.phase === "racing" || state.phase === "countdown") {
    if (embedded) return <RacePlay codeOverride={code} identity={identity} embedded />;
    nav(`/battle/play?code=${state.code}`, { replace: true });
    return <div className="full-center">Loading race…</div>;
  }

  const meInRoom = state.players.find((p) => p.id === me.id);
  // Ready-up step removed — joiners are auto-ready on the server, the
  // host is the only one who can start. Keep readyCount around for the
  // header counter ("N/M ready" still reads correctly = N/N).
  void meInRoom;
  const canStart = state.players.length >= 2;
  const isHost = state.hostId === me.id;
  // The lesson id can be either a seed-lesson id or the new
  // pack:packId::L<n> form. Resolve a friendly title + level index from
  // whichever it is.
  const packLessonMatch = /^pack:(.+?)::L(\d+)$/.exec(state.lessonId ?? "");
  let titleClean = "";
  let levelIndex: number | null = null;
  if (packLessonMatch) {
    const [, pId, lN] = packLessonMatch;
    const pack = pId ? getWordPack(pId) : undefined;
    const tlevel = getTeachingLevel(Number(lN));
    if (pack && tlevel) {
      titleClean = `${pack.title} • ${tlevel.title}`;
      levelIndex = tlevel.index;
    }
  } else {
    const lesson = lessons.find((l) => l.id === state.lessonId);
    titleClean = lesson?.title.replace(/^Level \d+:\s*/, "") ?? "";
    levelIndex = lesson?.orderIndex ?? null;
  }
  const cap = state.maxPlayers ?? MAX_PLAYERS;
  const isFull = state.players.length >= cap;

  const copyCode = () => {
    navigator.clipboard?.writeText(state.code).catch(() => {});
  };
  const leaveRoom = () => {
    client?.leave();
    clearSession();
    nav("/battle", { replace: true });
  };
  const pickLesson = (id: string) => {
    client?.changeLesson(id);
    setChangeLessonOpen(false);
  };

  // Build a fixed-size grid of slots: real players first, then empty placeholders
  // up to a visible total of 12 (3 rows × 4 cols). The room cap is still `cap`.
  // Auto-scaling slot count: show the actual roster + 1 "Open Slot"
  // teaser so it's obvious more friends can join. We don't render the
  // full `cap` (24) grid anymore — sitting with 23 empty boxes while
  // 1 player waits looked sparse and slowed the page on phones.
  const emptySlots = state.players.length < cap ? 1 : 0;

  const inviteShare = async () => {
    const url = `${window.location.origin}/battle?code=${state.code}`;
    try {
      if (navigator.share) await navigator.share({ title: "Word Runner Battle", text: `Join my battle! Code: ${state.code}`, url });
      else { await navigator.clipboard?.writeText(url); }
    } catch { /* ignore */ }
  };

  return (
    <>
      <div className="battle-v2-page">
        <div className="battle-v2-top">
          {embedded ? <span /> : (
            <button className="battle-v2-leave" onClick={leaveRoom}>
              <span aria-hidden>←</span> Leave
            </button>
          )}
          <div className="battle-v2-counter">
            <span aria-hidden>👥</span> {state.players.length}/{cap}
          </div>
          <button className="battle-v2-menu" aria-label="Menu">
            <LobbyFullscreenButton />
          </button>
        </div>

        <h1 className="battle-v2-title"><span aria-hidden>⚔️</span> Battle Lobby</h1>
        <p className="battle-v2-sub">
          Share the room code with your friends. Up to {cap} players.
        </p>

        <button className="battle-v2-code" onClick={copyCode} title="Tap to copy">
          <span className="battle-v2-code-label">ROOM CODE</span>
          <span className="battle-v2-code-value">{state.code}</span>
          <span className="battle-v2-code-copy"><span aria-hidden>📋</span> Tap to copy</span>
        </button>

        <div className="battle-v2-level">
          <span className="battle-v2-level-icon" aria-hidden>📘</span>
          <span className="battle-v2-level-text">
            Level {levelIndex ?? "?"} <span className="battle-v2-level-dot">•</span> {titleClean || "No lesson"}
          </span>
          {isHost && (
            <button className="battle-v2-level-change" onClick={() => setChangeLessonOpen(true)}>
              Change
            </button>
          )}
        </div>

        <div className="battle-v2-quiz">
          <p className="battle-quiz-label">Quiz style</p>
          {isHost ? (
            <div className="battle-quiz-row">
              {QUIZ_STYLES.map((q) => (
                <button
                  key={q.id}
                  className={`battle-quiz-chip ${(state.quizStyle ?? "default") === q.id ? "is-on" : ""}`}
                  onClick={() => client?.setQuizStyle(q.id)}
                  title={q.hint}
                >
                  <span aria-hidden>{q.icon}</span> {q.label}
                </button>
              ))}
            </div>
          ) : (
            <p className="battle-quiz-label" style={{ color: "#fff", marginTop: 0 }}>
              {getQuizStyle(state.quizStyle).icon} {getQuizStyle(state.quizStyle).label}
            </p>
          )}
        </div>

        <div className="battle-v2-roster">
          <div className="battle-v2-roster-head">
            <strong>Players</strong>
            <span>{state.players.length} in room</span>
          </div>
          <div className="battle-v2-grid">
            {state.players.map((p) => {
              const isMe = p.id === me.id;
              const isPlayerHost = p.id === state.hostId;
              return (
                <div key={p.id} className={`battle-v2-slot is-filled ${isMe ? "is-me" : ""}`}>
                  <div className="battle-v2-slot-avatar">
                    <AvatarViewer3D avatar={p.avatarConfig} />
                  </div>
                  <div className="battle-v2-slot-info">
                    <strong>{p.displayName}</strong>
                    <div className="battle-v2-slot-badges">
                      {isPlayerHost && <span className="battle-v2-badge battle-v2-badge-host">HOST</span>}
                      {isMe && <span className="battle-v2-badge battle-v2-badge-you">YOU</span>}
                    </div>
                  </div>
                </div>
              );
            })}
            {Array.from({ length: emptySlots }).map((_, i) => (
              <div key={`empty-${i}`} className="battle-v2-slot is-empty">
                <span className="battle-v2-slot-plus" aria-hidden>+</span>
                <span className="battle-v2-slot-emptyname">Open Slot</span>
              </div>
            ))}
          </div>
          <p className="battle-v2-roster-foot">
            <span aria-hidden>ⓘ</span> Players' 3D characters appear here when they join.
          </p>
          {isFull && (
            <p className="battle-v2-full-note">Room is full ({cap}). No more joins.</p>
          )}
        </div>

        <div className="battle-v2-actions">
          <button className="battle-v2-invite" onClick={inviteShare}>
            <span aria-hidden>👥+</span> Invite Friends
          </button>
          <button className="battle-v2-copy" onClick={copyCode}>
            <span aria-hidden>📋</span> Copy Code
          </button>
          {isHost ? (
            <button
              className="battle-v2-start"
              disabled={!canStart}
              onClick={() => client?.startRace()}
            >
              <span aria-hidden>⚔️</span> {canStart ? "Start Battle" : "Waiting…"}
            </button>
          ) : (
            <div className="battle-v2-waiting">⏳ Waiting for host…</div>
          )}
        </div>
        {!canStart && (
          <p className="battle-v2-foot">⌛ Waiting for more players…</p>
        )}
      </div>

      {changeLessonOpen && (
        <div className="home-mode-overlay" role="dialog" aria-modal="true" onClick={() => setChangeLessonOpen(false)}>
          <div
            className="home-mode-card"
            style={{ maxWidth: 760, padding: 18, position: "relative" }}
            onClick={(e) => e.stopPropagation()}
          >
            <button className="popup-close-x" onClick={() => setChangeLessonOpen(false)} aria-label="Close">×</button>
            <div style={{ maxHeight: "78vh", overflowY: "auto", overflowX: "hidden", paddingRight: 4 }}>
              <BattlePackPicker
                onPick={pickLesson}
                onCancel={() => setChangeLessonOpen(false)}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function LevelPicker({
  title,
  lessons,
  profile,
  onPick,
  onCancel,
  asOverlay,
}: {
  title: string;
  lessons: typeof SEED_LESSONS;
  profile: import("../local/profile").LocalProfile;
  onPick: (lessonId: string) => void;
  onCancel: () => void;
  asOverlay?: boolean;
}) {
  const inner = (
    <div className="lobby-picker">
      <div className="lobby-picker-header">
        <h2 className="t-title t-center" style={{ margin: 0 }}>{title}</h2>
        <button className="lobby-picker-close" onClick={onCancel} aria-label="Close">×</button>
      </div>
      <p className="t-body t-muted t-center" style={{ margin: 0 }}>
        Everyone races on the same level.
      </p>
      <div className="lobby-picker-grid">
        {lessons.map((l) => {
          const unlocked = isLessonUnlocked(profile, l.orderIndex, lessons);
          const titleClean = l.title.replace(/^Level \d+:\s*/, "");
          return (
            <button
              key={l.id}
              className={`lobby-picker-tile ${unlocked ? "" : "is-locked"}`}
              disabled={!unlocked}
              onClick={() => unlocked && onPick(l.id)}
            >
              <span className="lobby-picker-num">{l.orderIndex}</span>
              <span className="lobby-picker-name">{titleClean}</span>
              {!unlocked && <span className="lobby-picker-lock">🔒</span>}
            </button>
          );
        })}
      </div>
    </div>
  );

  if (asOverlay) {
    return (
      <div className="lobby-picker-overlay" onClick={onCancel}>
        <div onClick={(e) => e.stopPropagation()}>{inner}</div>
      </div>
    );
  }
  return <div className="lobby-shell">{inner}</div>;
}

interface OpenRoom {
  code: string;
  hostName: string;
  hostAvatar?: any;
  lessonId?: string;
  lessonTitle?: string;
  wordPack?: string;
  levelIndex?: number;
  playerCount: number;
  maxPlayers: number;
  phase: "lobby" | "countdown" | "racing" | "results";
  visibility: "open" | "private";
  updatedAt: number;
}

type RoomFilter = "all" | "waiting" | "racing";

function useOpenRooms(): { rooms: OpenRoom[]; loading: boolean; refresh: () => void } {
  const [rooms, setRooms] = useState<OpenRoom[]>([]);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const refresh = () => { setLoading(true); setTick((t) => t + 1); };
  useEffect(() => {
    let alive = true;
    const fetchOnce = async () => {
      try {
        const res = await fetch("/race/rooms", { cache: "no-store" });
        const json = (await res.json().catch(() => ({ rooms: [] }))) as { rooms?: OpenRoom[] };
        if (!alive) return;
        setRooms(Array.isArray(json.rooms) ? json.rooms : []);
      } catch {
        if (alive) setRooms([]);
      } finally {
        if (alive) setLoading(false);
      }
    };
    fetchOnce();
    // Poll fast (≈1.5s) so a freshly-created open game from any device
    // anywhere in the world appears almost immediately. Cloudflare KV
    // is globally replicated; the 5-minute TTL on each entry keeps the
    // list clean of crashed clients.
    const id = window.setInterval(fetchOnce, 1500);
    // Also refresh whenever the tab regains focus / visibility — so
    // returning to the lobby after a few seconds shows current rooms
    // instead of a stale list.
    const onVis = () => { if (!document.hidden) fetchOnce(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", fetchOnce);
    return () => {
      alive = false;
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", fetchOnce);
    };
  }, [tick]);
  return { rooms, loading, refresh };
}

function RaceEntry() {
  const nav = useNavigate();
  const [letters, setLetters] = useState<string[]>(["", "", "", ""]);
  const code = letters.join("");
  const [filter, setFilter] = useState<RoomFilter>("all");
  const [search, setSearch] = useState("");
  const { rooms, loading, refresh } = useOpenRooms();
  const setLetter = (idx: number, ch: string) => {
    const v = ch.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 1);
    setLetters((prev) => {
      const next = [...prev];
      next[idx] = v;
      return next;
    });
    if (v && idx < 3) {
      const nextInput = document.querySelector<HTMLInputElement>(`[data-codebox="${idx + 1}"]`);
      nextInput?.focus();
    }
  };
  const onKey = (idx: number) => (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !letters[idx] && idx > 0) {
      const prevInput = document.querySelector<HTMLInputElement>(`[data-codebox="${idx - 1}"]`);
      prevInput?.focus();
    }
  };
  const createOpen = () => {
    const c = newRoomCode();
    nav(`/battle?code=${c}&host=1&vis=open`);
  };
  const createPrivate = () => {
    const c = newRoomCode();
    nav(`/battle?code=${c}&host=1&vis=private`);
  };
  const submit = (e?: React.SyntheticEvent) => {
    e?.preventDefault();
    if (code.length === 4) nav(`/battle?code=${code}`);
  };
  const joinRoom = (c: string) => nav(`/battle?code=${c}`);

  const filteredRooms = rooms
    .filter((r) => {
      if (filter === "waiting") return r.phase === "lobby";
      if (filter === "racing")  return r.phase === "racing" || r.phase === "countdown";
      return true;
    })
    .filter((r) => {
      if (!search.trim()) return true;
      const s = search.toLowerCase();
      return (
        r.hostName.toLowerCase().includes(s) ||
        (r.lessonTitle ?? "").toLowerCase().includes(s) ||
        (r.wordPack ?? "").toLowerCase().includes(s)
      );
    });
  return (
    <div className="battle-entry-v2">
      <header className="battle-entry-top">
        <button className="battle-entry-menu" onClick={() => nav("/")}>
          <span aria-hidden>←</span> Menu
        </button>
        <div className="battle-entry-counter">
          <span aria-hidden>👥</span> Up to {MAX_PLAYERS} players
        </div>
        <button className="battle-entry-burger" aria-label="Menu">≡</button>
      </header>

      <h1 className="battle-entry-title">
        <span aria-hidden>⚔️</span> Battle
      </h1>
      <p className="battle-entry-sub">
        Race up to {MAX_PLAYERS} friends. Join open rooms or create your own.
      </p>

      {/* Top action card — Create + Join Private */}
      <div className="battle-entry-card battle-entry-actioncard">
        <div className="battle-entry-actiongrid">
          <button className="battle-entry-create" onClick={createOpen}>
            <span aria-hidden>🏁</span> Create a Room
          </button>
          <div className="battle-entry-private-block">
            <strong className="battle-entry-private-title">Join a Private Room</strong>
            <div className="battle-entry-codeboxes">
              {[0, 1, 2, 3].map((i) => (
                <input
                  key={i}
                  data-codebox={i}
                  className="battle-entry-codebox"
                  value={letters[i]}
                  onChange={(e) => setLetter(i, e.target.value)}
                  onKeyDown={onKey(i)}
                  maxLength={1}
                  inputMode="text"
                  autoCapitalize="characters"
                  aria-label={`Code letter ${i + 1}`}
                />
              ))}
            </div>
            <button className="battle-entry-join" onClick={submit} disabled={code.length < 4}>
              Join with Code
            </button>
          </div>
        </div>
        <div className="battle-entry-subactions">
          <button className="battle-entry-suboption" onClick={createOpen}>
            <span className="battle-entry-suboption-icon battle-entry-suboption-icon-blue" aria-hidden>🌐</span>
            <span><strong>Create</strong><small>Open Game</small></span>
          </button>
          <button className="battle-entry-suboption" onClick={createPrivate}>
            <span className="battle-entry-suboption-icon battle-entry-suboption-icon-purple" aria-hidden>🔒</span>
            <span><strong>Create</strong><small>Private Game</small></span>
          </button>
        </div>
      </div>

      {/* Open Games browser */}
      <div className="battle-entry-card battle-entry-browser">
        <header className="battle-entry-browser-head">
          <span className="battle-entry-browser-title">
            <span className="battle-entry-browser-icon" aria-hidden>🌐</span> Open Games
          </span>
          <input
            className="battle-entry-search"
            placeholder="Search games, word pack, or level"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="battle-entry-filterpills">
            {(["all", "waiting", "racing"] as const).map((f) => (
              <button
                key={f}
                className={`battle-entry-filterpill ${filter === f ? "is-active" : ""}`}
                onClick={() => setFilter(f)}
              >
                {f === "all" ? "All" : f === "waiting" ? "Waiting" : "In Progress"}
              </button>
            ))}
            <button
              className={`battle-entry-refresh ${loading ? "is-loading" : ""}`}
              onClick={refresh}
              aria-label="Refresh open games"
              title="Refresh"
            >
              <span aria-hidden>↻</span>
            </button>
          </div>
        </header>
        <p className="battle-entry-browser-sub">
          <span className="battle-entry-live-dot" aria-hidden /> Live worldwide — open games from any device, anywhere. Join any open game, even if it's already in progress.
        </p>

        <div className="battle-entry-browser-list">
          {loading && (
            <div className="battle-entry-empty">Loading rooms…</div>
          )}
          {!loading && filteredRooms.length === 0 && (
            <div className="battle-entry-empty">
              No open games right now. <button className="battle-entry-empty-link" onClick={createOpen}>Create one →</button>
            </div>
          )}
          {filteredRooms.map((r) => {
            const status: "waiting" | "racing" = r.phase === "lobby" ? "waiting" : "racing";
            return (
              <div key={r.code} className="battle-entry-row">
                <span className="battle-entry-row-avatar" aria-hidden>👤</span>
                <strong className="battle-entry-row-name">{r.hostName}'s Game</strong>
                <span className="battle-entry-row-level">
                  <span aria-hidden>🏔</span> Level {r.levelIndex ?? "?"}{r.lessonTitle ? `: ${r.lessonTitle}` : ""}
                </span>
                <span className="battle-entry-row-pack">
                  <span aria-hidden>📚</span> Word Pack: {r.wordPack ?? "Mixed"}
                </span>
                <span className="battle-entry-row-players">
                  <span aria-hidden>👥</span> {r.playerCount}/{r.maxPlayers}
                </span>
                <span className={`battle-entry-row-status status-${status}`}>
                  {status === "waiting" ? "Waiting" : "In Progress"}
                </span>
                <button className="battle-entry-row-join" onClick={() => joinRoom(r.code)}>
                  Join
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
